/**
 * Template fix-ups for the order sheet.
 *
 * Both of these used to walk the live TinyMCE body with a TreeWalker and mutate
 * it in place. ProseMirror owns its DOM, so that is no longer allowed - and it
 * is not needed either. They are pure transforms on the sheet's HTML now: the
 * caller applies one and hands the result back to the editor as its value.
 */

const parse = (html) => {
  try {
    const doc = new DOMParser().parseFromString(
      `<div id="accb-order-root">${String(html || "")}</div>`,
      "text/html"
    );
    return doc.getElementById("accb-order-root");
  } catch (err) {
    return null;
  }
};

const serialise = (root) => (root ? root.innerHTML : "");

const findTextNode = (root, test) => {
  const walker = root.ownerDocument.createTreeWalker(
    root,
    NodeFilter.SHOW_TEXT,
    null,
    false
  );
  let node = null;
  while ((node = walker.nextNode())) {
    if (test(node.nodeValue || "", node)) return node;
  }
  return null;
};

/**
 * Every page of the sheet has its own "Post this matter" link, and a booking
 * made through one must land in that link alone. So each link carries an id
 * in its href - a query parameter, because the href is the one thing that
 * survives the editor, the save and the .docx; Book Hearing Room ignores it.
 */
export const LINK_ID_PARAM = "post_link";

export const isPostMatterText = (text) => /Post this matter/i.test(text || "");

/** The link id in an href, or "" when it has none. */
export const linkIdOf = (href) => {
  const match = new RegExp(`[?&]${LINK_ID_PARAM}=([^&#]*)`).exec(href || "");
  return match ? decodeURIComponent(match[1]) : "";
};

/** The href with its link id set to `id`, or removed when `id` is empty. */
export const withLinkId = (href, id) => {
  const value = String(href || "");
  const hashAt = value.indexOf("#");
  const hash = hashAt === -1 ? "" : value.slice(hashAt);
  const rest = hashAt === -1 ? value : value.slice(0, hashAt);
  const queryAt = rest.indexOf("?");
  const path = queryAt === -1 ? rest : rest.slice(0, queryAt);
  const params = queryAt === -1
    ? []
    : rest
      .slice(queryAt + 1)
      .split("&")
      .filter((param) => param && param.split("=")[0] !== LINK_ID_PARAM);
  if (id) params.push(`${LINK_ID_PARAM}=${encodeURIComponent(id)}`);
  return path + (params.length ? `?${params.join("&")}` : "") + hash;
};

const linkAnchors = (root, id) =>
  Array.from(root.querySelectorAll("a[href]")).filter(
    (anchor) => linkIdOf(anchor.getAttribute("href")) === id
  );

/**
 * The signature lines of the order sheet template: everything under the
 * "Post this matter" line in its order column, as HTML. The backend builds
 * the template from the case's tribunal, so this is the signature block as
 * it should be, whatever has since been typed into the sheet. "" if the
 * template has no such line.
 */
export const templateSignature = (html) => {
  const root = parse(html);
  const anchor =
    root &&
    Array.from(root.querySelectorAll("a[href]")).find((a) =>
      isPostMatterText(a.textContent)
    );
  const cell = anchor && anchor.closest("td, th");
  if (!cell) return "";
  let line = anchor;
  while (line.parentElement !== cell) line = line.parentElement;
  const lines = [];
  for (let node = line.nextElementSibling; node; node = node.nextElementSibling) {
    lines.push(node.outerHTML);
  }
  return lines.join("");
};

// Either spelling of the break PageBreak.js writes (see BREAK_PROPS there).
const PAGE_START =
  /(?:^|;)\s*(?:page-break-before\s*:\s*always|break-before\s*:\s*page)/i;

/**
 * Which page of `container` (a table cell, or the whole sheet) `node` is on:
 * the number of page breaks before it in that container, its own block's
 * included.
 */
const pageOf = (node, container) => {
  const owner = container.matches("td, th") ? container : null;
  let page = 0;
  container.querySelectorAll("[style]").forEach((element) => {
    if (!PAGE_START.test(element.getAttribute("style"))) return;
    if (element.closest("td, th") !== owner) return;
    if (
      element === node ||
      element.contains(node) ||
      element.compareDocumentPosition(node) & Node.DOCUMENT_POSITION_FOLLOWING
    ) {
      page += 1;
    }
  });
  return page;
};

/**
 * Drop the backend-built "Post this matter on ... for ..." line into the sheet.
 * The anchor and href come entirely from the backend; this only finds the line
 * and swaps its HTML in.
 *
 * With `linkId`, only the link carrying that id is replaced, and the new one
 * keeps the id; the other pages' links are left as they are. Without it, the
 * first line mentioning "Post this matter" is replaced, as before links had
 * ids.
 *
 * @param {string} html the order sheet
 * @param {string} replacement the backend's HTML for that line
 * @param {string} [linkId] the id of the link that was booked through
 * @returns {string} the sheet, unchanged if the line is not present
 */
export const applyPostMatterHtml = (html, replacement, linkId) => {
  const root = parse(html);
  if (!root || !replacement) return String(html || "");

  if (linkId) {
    const anchors = linkAnchors(root, linkId);
    if (!anchors.length) return String(html || "");
    const holder = root.ownerDocument.createElement("div");
    holder.innerHTML = replacement;
    holder.querySelectorAll("a[href]").forEach((anchor) => {
      anchor.setAttribute("href", withLinkId(anchor.getAttribute("href"), linkId));
    });
    // A link the editor split into several anchors becomes one again.
    const [first, ...rest] = anchors;
    rest.forEach((anchor) => anchor.remove());
    while (holder.firstChild) first.parentNode.insertBefore(holder.firstChild, first);
    first.remove();
    return serialise(root);
  }

  const node = findTextNode(root, isPostMatterText);
  if (!node) return String(html || "");

  const paragraph = node.parentElement && node.parentElement.closest("p");
  const target = paragraph || node.parentElement;
  if (target) target.innerHTML = replacement;

  return serialise(root);
};

/**
 * Fill the "Posted for {{Stage}}" line from the booked stage, or reset it to
 * the placeholder when there is none.
 *
 * With `linkId`, only the "Posted for" line on the same page as that link
 * (in the same table row) is filled; a page without one is left alone.
 */
export const applyPostedForStage = (html, stageName, linkId) => {
  const root = parse(html);
  if (!root) return String(html || "");

  const postedFor = (text) => /^\s*Posted for\b/.test(text);
  let test = postedFor;
  let scope = root;
  if (linkId) {
    const anchor = linkAnchors(root, linkId)[0];
    if (!anchor) return String(html || "");
    const cell = anchor.closest("td, th");
    const page = pageOf(anchor, cell || root);
    scope = cell ? cell.parentElement : root;
    test = (text, node) =>
      postedFor(text) &&
      pageOf(node, node.parentElement.closest("td, th") || root) === page;
  }

  const node = findTextNode(scope, test);
  if (!node) return String(html || "");

  node.nodeValue = `Posted for ${stageName || "{{Stage}}"}`;
  return serialise(root);
};
